import {
  QueryClient,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { FetchCurrentShiftQuery, EndShiftMut, AckShiftMut } from '../api/shifts/shift';
import { useCallback, useMemo } from 'react';
import axios from 'axios';
import { INACTIVE_PROMPT_ADVANCE_MS, INACTIVE_TIMEOUT_MS } from './util';
import { getDefaultState, ShiftState, useShiftStore } from './shift-store';
import { toast } from '@/hooks/use-toast';
import { addMilliseconds, subMilliseconds } from 'date-fns';
import { useAuthStore } from '@/store/authStore';
import { useShiftBroadcastChannel } from './shift-broadcast';

const useAckShiftMut_ = (queryClient?: QueryClient) => {
  return useMutation({
    ...AckShiftMut(),
    onSuccess(data) {
      queryClient?.setQueryData(FetchCurrentShiftQuery().queryKey, (existing: any) => ({
        ...existing,
        shift: data?.shift,
      }));
    },
    onError(err) {
      console.error('Error while ACKing shift', err);
    },
    retry(failureCount) {
      return failureCount < 2;
    },
  });
};

const useEndShiftMut_ = (queryClient?: QueryClient) => {
  return useMutation({
    ...EndShiftMut(),
    onError() {
      toast({
        title: 'An error occurred while ending shift.',
        variant: 'destructive',
      });
    },
    onMutate() {
      queryClient?.setQueryData(FetchCurrentShiftQuery().queryKey, (existing: any) => ({
        ...existing,
        shift: undefined,
      }));
    },
    retry(failureCount) {
      return failureCount < 2;
    },
  });
};

export const useShiftControls = () => {
  const queryClient = useQueryClient();
  const endShiftMut = useEndShiftMut_(queryClient);
  const ackShiftMut = useAckShiftMut_(queryClient);
  const channel = useShiftBroadcastChannel();

  const resetStore = useCallback(() => {
    useShiftStore.setState(getDefaultState);
  }, []);

  const startShift = useCallback(() => {
    // Reset the state
    useShiftStore.setState(getDefaultState);
    // Start the shift
    ackShiftMut.mutateAsync();
    channel.sendMessage({ type: 'shift.start', state: useShiftStore.getState() });
  }, [ackShiftMut.mutateAsync, channel.sendMessage]);

  const endShift = useCallback(
    async (opts?: { clean?: boolean }) => {
      const clean = opts?.clean ?? false;

      console.debug('Ending shift. Cleaning store:', clean);
      await endShiftMut.mutateAsync().catch(console.error);

      if (clean) {
        useShiftStore.setState(getDefaultState);
      }
      channel.sendMessage({ type: 'shift.end', state: useShiftStore.getState() });
    },
    [endShiftMut.mutateAsync, channel.sendMessage]
  );

  const startBreak = useCallback(async () => {
    console.debug('Starting break');
    useShiftStore.setState({ breakStartedAt: new Date() });
    await endShiftMut.mutateAsync().catch(console.error);
    channel.sendMessage({ type: 'break.start', state: useShiftStore.getState() });
  }, [endShiftMut.mutateAsync, channel.sendMessage]);

  const endBreak = useCallback(async () => {
    console.debug('Ending break');
    await ack();
    useShiftStore.setState({ breakStartedAt: undefined });
    channel.sendMessage({ type: 'break.end', state: useShiftStore.getState() });
  }, [ackShiftMut.mutateAsync, channel.sendMessage]);

  const ack = useCallback(async () => {
    const { status, lastActiveAt } = useShiftStore.getState();

    const updates: Partial<ShiftState> = {};

    if (status !== 'active') {
      updates.status = 'active';
    }

    if (lastActiveAt != null && lastActiveAt < new Date()) {
      updates.lastActiveAt = new Date();
    }

    if (Object.keys(updates).length) {
      useShiftStore.setState(updates);
    }

    await ackShiftMut.mutateAsync();
  }, [ackShiftMut.mutateAsync]);

  return {
    endShift,
    ack,
    endBreak,
    startBreak,
    startShift,
    resetStore,
  };
};

export const useCurrentShift = () => {
  const isAuthenticatedMonitor = useAuthStore((s) => s.isAuthenticatedMonitor);
  const lastActiveAt = useShiftStore((s) => s.lastActiveAt);
  const status = useShiftStore((s) => s.status);
  const isEnding =
    useIsMutating({
      mutationKey: EndShiftMut().mutationKey,
      exact: true,
    }) > 0;
  const isAcking =
    useIsMutating({
      mutationKey: AckShiftMut().mutationKey,
      exact: true,
    }) > 0;
  const queryClient = useQueryClient();
  const endShiftMut = useEndShiftMut_(queryClient);
  const ackShiftMut = useAckShiftMut_(queryClient);
  const shiftQuery = useQuery({
    ...FetchCurrentShiftQuery(),
    retry(failureCount) {
      return failureCount < 2;
    },
    enabled: isAuthenticatedMonitor,
    refetchInterval: Math.floor(INACTIVE_TIMEOUT_MS / 3),
  });
  const shift = shiftQuery.data?.shift;
  const requiresShift = shiftQuery.data?.requiresShift;

  const isLoading = isEnding || isAcking || shiftQuery.isLoading;

  // We want to use "canonical" dates here to help circumvent the issues with setTimout
  // and idlenes.
  // Setting these values as a function of last active ensures it's always exactly correct.
  const [inactiveAt, idleAt] = useMemo(() => {
    const idleAt = addMilliseconds(lastActiveAt, INACTIVE_TIMEOUT_MS);
    const inactiveAt = subMilliseconds(idleAt, INACTIVE_PROMPT_ADVANCE_MS);

    return [inactiveAt, idleAt] as [Date, Date];
  }, [lastActiveAt]);

  return {
    shift,
    canStartShift: isAuthenticatedMonitor,
    requiresShift: requiresShift ?? true,
    isEnding,
    isLoading,
    endError: axios.isAxiosError(endShiftMut.error) ? endShiftMut.error : undefined,
    startError: axios.isAxiosError(ackShiftMut.error) ? ackShiftMut.error : undefined,
    lastActiveAt,
    /** The idle start time, which may be in the past or future */
    idleAt,
    /** The inactive start time which precedes idleAt, which may be in the past or future */
    inactiveAt,
    status,
  };
};
